import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Role, CreateRoleRequest } from '../../services/roleService';
import { usePermissions } from '../../hooks/useRoles';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { PermissionMatrix } from './PermissionMatrix';

interface RoleFormProps {
  role?: Role;
  onSubmit: (data: CreateRoleRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const RoleForm: React.FC<RoleFormProps> = ({
  role,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { permissions, isLoading: isLoadingPermissions } = usePermissions();
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: role?.name || '',
      description: role?.description || '',
    },
  });

  useEffect(() => {
    if (role) {
      setSelectedPermissions(role.permissions.map((p) => p.id));
      reset({
        name: role.name,
        description: role.description,
      });
    }
  }, [role, reset]);

  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    if (checked) {
      setSelectedPermissions((prev) => [...prev, permissionId]);
    } else {
      setSelectedPermissions((prev) => prev.filter((id) => id !== permissionId));
    }
  };

  const handleFormSubmit = (data: any) => {
    onSubmit({
      ...data,
      permissionIds: selectedPermissions,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <Input
          label="Role Name"
          {...register('name', {
            required: 'Role name is required',
            minLength: {
              value: 3,
              message: 'Role name must be at least 3 characters',
            },
          })}
          error={errors.name?.message}
          disabled={role && ['admin', 'employee', 'manager', 'hr'].includes(role.name.toLowerCase())}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            {...register('description')}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            disabled={role && ['admin', 'employee', 'manager', 'hr'].includes(role.name.toLowerCase())}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Permissions</h3>
        {isLoadingPermissions ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <PermissionMatrix
            permissions={permissions}
            selectedPermissions={selectedPermissions}
            onChange={handlePermissionChange}
            disabled={role && ['admin', 'employee', 'manager', 'hr'].includes(role.name.toLowerCase())}
          />
        )}
      </div>

      {role && ['admin', 'employee', 'manager', 'hr'].includes(role.name.toLowerCase()) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <p className="text-sm text-yellow-800">
            This is a system role. Permissions cannot be modified.
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || (role && ['admin', 'employee', 'manager', 'hr'].includes(role.name.toLowerCase()))}
        >
          {role ? 'Update Role' : 'Create Role'}
        </Button>
      </div>
    </form>
  );
};